import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { HeaderNav } from '../components/HeaderNav';
import { roadmapApi } from '../services/api';
import {
  ROADMAP_SCHEMA_VERSION,
  type AlignmentRow,
  type Citation,
  type Gap,
  type GapSeverity,
  type LaneReading,
  type PlannedItem,
  type Roadmap,
} from '../roadmapTypes';

/**
 * The five-lane roadmap (ROADMAP_APP.md Phase 2b).
 *
 * WHAT THIS PAGE IS FOR
 *
 * Five lanes describe the same platform from five places that can disagree:
 *
 *   CLAIMED    ADP-PLATFORM-OVERVIEW.md §7 counts and §11 remaining work
 *   PLANNED    NORTH-STAR.md §4.1 (and queue.json, once Phase 0 publishes it)
 *   SHIPPED    releases.json
 *   DEPLOYED   releases-stamps.json
 *   VERIFIED   the north-star checker (Phase 4 — not wired yet, and says so)
 *
 * The lanes are the inputs. **The deliverable is the disagreement**, which is why the
 * gaps list is the first thing on the page and the lane strip is a thin header above it.
 * The design commitment this page is built to honour: *if a lane cannot disagree with
 * another lane, it does not belong here. A roadmap that cannot go red is a deck.*
 *
 * THREE RENDERING RULES, EACH ONE LOAD-BEARING
 *
 * 1. **Absence is never drawn as zero.** A lane that did not read renders the word
 *    `unmeasured` (or `absent`) in its own visual register, never `0 rows`. This is the
 *    same rule the generator follows, and it has to survive the trip to the screen: a
 *    grey "0" is a confident negative, and a confident negative about work nobody
 *    measured is the failure mode this whole page exists to catch. `north-star-check.sh`
 *    shipped six defects of exactly this shape (NORTH-STAR.md §4.4).
 *
 * 2. **Age is computed HERE, never read from the payload.** `roadmap.json` carries
 *    `deployed.stampedAt` and no `ageDays`, no `stale` boolean, no `generatedAt` — by
 *    design, because a wall-clock field in a committed artifact makes it differ on every
 *    run and costs the drift gate (`roadmap_build.py --check`) its meaning. So `ageText()`
 *    below does the subtraction at render time, exactly as `releases.html` already does.
 *    If you are ever tempted to move staleness into the generator: that change turns a
 *    byte-comparable artifact into one that is red every morning with no commit to blame,
 *    and a gate that reds without a diff gets routed around within a week.
 *
 * 3. **`unowned` and `undated` are first-class states, not empty cells.** An open
 *    commitment with no owner is a note, not a plan — so it renders as a labelled state,
 *    in the same visual weight as a gap, rather than as blank space the eye skips.
 *
 * ON COLOURS: `viz-src/README.md` records that opacity modifiers on the semantic colour
 * tokens silently emit NOTHING — `bg-error/10` produces no rule at all, so the element
 * renders with a transparent background and the severity signal disappears with no build
 * error and no console warning. Every tint below is therefore a SOLID `*-bg` token
 * (`bg-error-bg`, `bg-warning-bg`, `bg-info-bg`, `bg-success-bg`). Do not "simplify" these
 * to `/10` variants.
 *
 * ON CONTRAST — the colours here were MEASURED, and three of them were wrong. A tint is
 * not a licence for its own foreground: `text-warning` on `bg-warning-bg` measured 1.82:1
 * in light mode, which is a signal you can see is there and cannot read, and it carried
 * every `unowned` badge and every MEDIUM gap. Every chip's foreground is therefore theme
 * text; the tint and the border carry the colour. Section headings are theme text too —
 * `text-tower-accent` (`--dt-primary` #00843d) is the same value in both themes, so at
 * 14px it measures 4.50:1 on the light surface and 3.21:1 on the dark one, i.e. it passes
 * light by a rounding margin and fails dark. A heading reads as a heading from its weight,
 * case, tracking and mono face; the accent was carrying nothing those four did not. The
 * app SHELL (the MAESTRO link, the breadcrumb) keeps the accent at ~3.6:1: it is shared
 * with VizPage and HeaderNav, and forking it on one page trades a contrast defect for an
 * inconsistency defect — `check-roadmap.py` prints that number every run rather than
 * exempting it silently, and gates WCAG AA on this page's own `<main>` in both themes.
 *
 * ON FILTERS: ROADMAP_APP.md Phase 2b asks for filters by theme, product and gap type.
 * Gap type and severity are here. Theme and product are NOT — the schema-1 artifact has
 * no theme or product field on any row, so those two filters would have had to invent a
 * taxonomy at render time and then silently mis-bucket rows that did not match it. A
 * free-text filter over title/detail/source does the job those were reaching for without
 * asserting a classification the data does not carry. When the generator grows real theme
 * and product fields, replace the text box with them.
 */

const SEVERITY_ORDER: GapSeverity[] = ['high', 'medium', 'info'];

/** Solid tints only — see the opacity-modifier note in the file docstring. */
const SEVERITY_STYLE: Record<GapSeverity, { chip: string; row: string; label: string }> = {
  high: { chip: 'bg-error-bg text-tower-text border-error', row: 'border-l-error', label: 'HIGH' },
  medium: { chip: 'bg-warning-bg text-tower-text border-warning', row: 'border-l-warning', label: 'MEDIUM' },
  info: { chip: 'bg-info-bg text-tower-text border-info', row: 'border-l-info', label: 'INFO' },
};

/**
 * How long ago, from an ISO instant. Returns null for a missing instant rather than a
 * plausible "0d ago", which is rule 1 applied to a single field: we do not know the age
 * of a stamp that was never written, and saying "today" would be a fabricated reading.
 */
function ageText(iso: string | undefined): string | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days < 0) return 'stamped in the future';
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

/**
 * PLANNED-past-review-without-SHIPPED — §4's second renderable gap, computed HERE.
 *
 * This is not an oversight in the generator; it is rule 2 of this file's docstring applied to
 * the one gap that is purely a function of the clock. `roadmap_build.py` deliberately does not
 * emit `planned-expired`: writing it would make `--check` go red on the morning an item
 * expires, with no commit to blame and no edit that fixes it except regenerating a file to
 * record the passage of time — and a drift gate that reds without a diff is one people learn
 * to bypass. So the artifact carries `reviewBy`, and the subtraction happens on screen.
 *
 * `null` review dates are EXCLUDED rather than treated as expired. An item whose date could
 * not be parsed is already reported as `planned-undated` by the generator, and counting it
 * here too would report one defect twice.
 */
function expiredItems(items: PlannedItem[], now: number): PlannedItem[] {
  return items.filter((i) => {
    const rb = i.planned?.reviewBy;
    if (!rb || !i.planned?.reviewByParsed) return false;
    const due = Date.parse(`${rb}T00:00:00Z`);
    return !Number.isNaN(due) && due < now;
  });
}

/**
 * The artifacts a commitment cites, and whether they are in the tree.
 *
 * Rendered beside an expired item because of a specific, repeated failure: a commitment whose
 * whole premise is "`gate.sh` has never been committed to any branch" stays open until someone
 * re-reads the premise, and nobody does. Showing `resolved` next to that citation is the cheap
 * prompt to check whether the blocker cleared itself.
 *
 * `unresolved` is rendered as its own word and never as a red cross: a path that does not
 * resolve is usually a sibling repo that is not checked out here, not a missing artifact.
 */
function Cites({ cites }: { cites?: Citation[] }) {
  // `undefined` and `[]` are both "nothing to draw" for a CHIP ROW specifically, and that is
  // the one place they may be collapsed: the absence-is-not-a-zero rule governs measurements,
  // and this is not one. A Phase-2 artifact carries no `cites` key at all; an item that cites
  // nothing carries an empty list. Neither is a claim that the item has no evidence.
  if (!cites || cites.length === 0) return null;
  return (
    <span className="inline-flex gap-1 flex-wrap ml-1 align-middle">
      {cites.map((c) => (
        <span
          key={c.cite}
          data-cite={c.cite}
          data-cite-state={c.state}
          title={
            c.state === 'resolved'
              ? `resolves to ${c.path}${c.ambiguous ? ' (and others — the citation is ambiguous)' : ''}`
              : c.state === 'glob'
                ? 'a glob, not a path — not resolvable by name'
                : 'does not resolve in this checkout; most likely a sibling repo'
          }
          // THREE states, three appearances. `glob` and `unresolved` shared the info tint
          // until check-roadmap.py measured them byte-identical — they differed only in a
          // `title` attribute, i.e. not at all for anyone reading the page. They are the
          // "we could not look" / "we looked and it is not there" distinction that the rest
          // of this artifact is built on, so collapsing them here contradicted the page.
          className={`text-[10px] font-mono px-1 py-0.5 rounded ${
            c.state === 'resolved'
              ? 'bg-success-bg text-tower-text border border-success'
              : c.state === 'unresolved'
                ? 'bg-info-bg text-tower-text border border-info'
                : // A glob was never resolvable by name, so it gets no verdict colour at
                  // all: plain surface, dashed outline. Neither a pass nor a finding.
                  'bg-tower-surface text-tower-muted border border-dashed border-tower-border'
          }`}
        >
          {c.cite}
          {c.ambiguous && '*'}
        </span>
      ))}
    </span>
  );
}

/** A lane header cell. The point of this component is the else-branch. */
function LaneCell({ name, reading, note }: { name: string; reading: LaneReading; note?: string }) {
  const measured = reading.state === 'read';
  // On the tinted panel the SECONDARY text colour is not readable: `--dt-text-secondary`
  // on `--dt-warning-bg` measures 3.27:1 in dark mode (#9fa4a2 on #7a3f00). That is below
  // 4.5:1 for 10px text and it is the label that says which lane you are looking at. A
  // tint changes what may sit on it, so the subordinate text promotes with the panel.
  const sub = measured ? 'text-tower-muted' : 'text-tower-text';
  return (
    <div
      className={`p-3 rounded-lg border ${
        measured ? 'bg-tower-surface border-tower-border' : 'bg-warning-bg border-warning'
      }`}
    >
      <div className={`text-[10px] uppercase tracking-wider ${sub} font-mono`}>{name}</div>
      {measured ? (
        <div className="text-lg font-semibold text-tower-text">
          {reading.rows}
          <span className="text-xs text-tower-muted font-normal ml-1">rows</span>
        </div>
      ) : (
        // NOT `{reading.rows ?? 0}`. A lane that did not read has no count, and printing
        // a zero here would turn "nobody looked" into "we looked and found nothing".
        // The state WORD is theme text, not `text-warning`: warning-on-warning-bg measured
        // 1.82:1 in light mode, which is a signal you can see is there and cannot read.
        // The panel's tint and border carry the colour; the word carries the meaning.
        <div className="text-sm font-semibold text-tower-text" data-state={reading.state}>
          {reading.state}
        </div>
      )}
      {(reading.detail || reading.phase || note) && (
        <div className={`text-[10px] ${sub} mt-1 leading-snug`}>
          {reading.detail || note}
          {reading.phase && <span className="ml-1 font-mono">({reading.phase})</span>}
        </div>
      )}
    </div>
  );
}

/**
 * One of ROADMAP_APP.md §4's four directed disagreements (plus VERIFIED) — the alignment
 * view (Phase 3).
 *
 * The else-branch is the component. A direction the generator could not compute renders the
 * word `unmeasured` and the input that would fix it; it must never render `0`. Those two
 * states look identical in a flat gap list — both are "nothing under this heading" — and the
 * whole thesis of this page is that a confident zero about something nobody measured is the
 * most expensive kind of wrong. `count` is typed `number | null` so that `?? 0` is a visible
 * decision rather than an accident.
 *
 * Clicking a measured direction filters the gap list to the types that direction owns, which
 * is what makes this a view over the gaps rather than a second, disagreeing summary of them.
 */
function AlignmentCell({
  row,
  active,
  extra,
  onSelect,
}: {
  row: AlignmentRow;
  active: boolean;
  /** Gaps this direction owns that only the CLOCK can produce — see `expired` below. */
  extra?: { count: number; label: string };
  onSelect: () => void;
}) {
  const measured = row.state === 'measured';
  const total = (row.count ?? 0) + (extra?.count ?? 0);
  const clean = measured && total === 0;
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={!measured}
      data-alignment={row.id}
      data-state={row.state}
      aria-pressed={active}
      className={`text-left p-3 rounded-lg border transition-colors ${
        !measured
          ? 'bg-warning-bg border-warning cursor-default'
          : active
            ? 'bg-primary-bg border-tower-accent'
            : clean
              ? 'bg-success-bg border-success hover:border-tower-accent'
              : 'bg-tower-surface border-tower-border hover:border-tower-accent'
      }`}
    >
      <div className="text-[10px] uppercase tracking-wider font-mono text-tower-text">
        {row.from} <span aria-hidden="true">→</span> {row.to}
      </div>
      {measured ? (
        <div className="text-lg font-semibold text-tower-text">
          {total}
          <span className="text-xs text-tower-text font-normal ml-1">
            {total === 1 ? 'gap' : 'gaps'}
          </span>
          {extra && extra.count > 0 && (
            <span className="text-[10px] font-mono text-tower-text ml-1">
              (incl. {extra.count} {extra.label})
            </span>
          )}
        </div>
      ) : (
        // NOT `{row.count ?? 0}`. See the component docstring.
        <div className="text-sm font-semibold text-tower-text" data-unmeasured={row.id}>
          unmeasured
        </div>
      )}
      <div className="text-[10px] text-tower-text mt-1 leading-snug">{row.title}</div>
      {!measured && row.blockedBy && (
        <div className="text-[10px] text-tower-text mt-1 leading-snug italic">{row.blockedBy}</div>
      )}
    </button>
  );
}

function GapRow({ gap }: { gap: Gap }) {
  const style = SEVERITY_STYLE[gap.severity] ?? SEVERITY_STYLE.info;
  return (
    <div className={`border-l-4 ${style.row} bg-tower-surface border border-tower-border rounded-r-lg p-4`}>
      <div className="flex items-start gap-3 flex-wrap">
        <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded border ${style.chip}`}>
          {style.label}
        </span>
        <span className="text-[10px] font-mono text-tower-muted border border-tower-border rounded px-1.5 py-0.5">
          {gap.type}
        </span>
        <span className="text-sm font-semibold text-tower-text flex-1 min-w-[12rem]">{gap.title}</span>
        {gap.claimed !== undefined && gap.measured !== undefined && (
          <span className="text-xs font-mono text-error">
            claimed {gap.claimed} / measured {gap.measured}
          </span>
        )}
      </div>
      <p className="text-xs text-tower-muted leading-relaxed mt-2">{gap.detail}</p>
      {gap.cites && gap.cites.length > 0 && (
        <p className="text-[11px] text-tower-muted mt-1">
          Cited source of truth:
          <Cites cites={gap.cites} />
        </p>
      )}
      {gap.nearest && (
        <p className="text-[11px] text-tower-muted mt-1 italic">
          Nearest baseline label: <span className="font-mono text-tower-text">{gap.nearest}</span>
          {gap.nearestValue !== null && gap.nearestValue !== undefined && ` = ${gap.nearestValue}`}
          {' '}— a hint for a human, not a join.
        </p>
      )}
    </div>
  );
}

function ItemRow({ item }: { item: PlannedItem }) {
  const p = item.planned;
  const unowned = p ? !p.owned : false;
  const undated = p ? !p.reviewByParsed : false;
  return (
    <tr className="border-b border-tower-border align-top">
      <td className="py-2 pr-3">
        <div className="text-sm text-tower-text">{item.title}</div>
        {item.detail && <div className="text-[11px] text-tower-muted leading-snug mt-0.5">{item.detail}</div>}
        {/* The cited artifacts belong HERE, on the live commitment, not only on the expired
            section below. Rendering them only once a date has passed means the page shows a
            commitment's evidence exactly when it is too late to act on it — and three of
            today's commitments cite paths that do not resolve in this checkout, which no
            reader of this table could currently tell. */}
        <Cites cites={item.cites} />
      </td>
      <td className="py-2 pr-3 whitespace-nowrap">
        <span className="text-[10px] font-mono text-tower-muted">{item.source}</span>
      </td>
      <td className="py-2 pr-3 whitespace-nowrap">
        {p ? (
          <span className="text-xs text-tower-text font-mono">{p.declaredState || p.state}</span>
        ) : item.claimed ? (
          <span className="text-xs text-tower-muted font-mono">{item.claimed.state}</span>
        ) : null}
      </td>
      <td className="py-2 pr-3 whitespace-nowrap">
        {unowned ? (
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-warning-bg text-tower-text border border-warning">
            unowned
          </span>
        ) : (
          <span className="text-xs text-tower-text">{p?.owner ?? '—'}</span>
        )}
      </td>
      <td className="py-2 pr-3 whitespace-nowrap">
        {undated ? (
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-warning-bg text-tower-text border border-warning">
            undated
          </span>
        ) : (
          <span className="text-xs font-mono text-tower-text">{p?.reviewBy}</span>
        )}
      </td>
      <td className="py-2 whitespace-nowrap">
        {/* VERIFIED is Phase 4. Until the checker emits JSON every row here reads
            `unmeasured`, and that is the honest answer — not a green tick by default. */}
        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-info-bg text-tower-text border border-info">
          {item.verified?.state ?? 'unmeasured'}
        </span>
      </td>
    </tr>
  );
}

export function RoadmapPage() {
  const [data, setData] = useState<Roadmap | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [severities, setSeverities] = useState<Set<GapSeverity>>(new Set(SEVERITY_ORDER));
  const [types, setTypes] = useState<Set<string>>(new Set());
  const [text, setText] = useState('');

  useEffect(() => {
    let live = true;
    roadmapApi
      .get()
      .then((d) => live && setData(d))
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, []);

  const allTypes = useMemo(
    () => (data ? [...new Set(data.gaps.map((g) => g.type))].sort() : []),
    [data],
  );

  const gaps = useMemo(() => {
    if (!data) return [];
    const q = text.trim().toLowerCase();
    return data.gaps
      .filter((g) => severities.has(g.severity))
      .filter((g) => types.size === 0 || types.has(g.type))
      .filter((g) => !q || `${g.title} ${g.detail} ${g.type}`.toLowerCase().includes(q))
      .sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity));
  }, [data, severities, types, text]);

  const items = useMemo(() => {
    if (!data) return [];
    const q = text.trim().toLowerCase();
    const all = [...data.items.planned, ...data.items.claimedRemaining];
    return q
      ? all.filter((i) => `${i.title} ${i.detail ?? ''} ${i.source}`.toLowerCase().includes(q))
      : all;
  }, [data, text]);

  // The clock is read ONCE per render pass and threaded down, rather than each row calling
  // Date.now(). Two rows evaluating on either side of midnight would disagree about the same
  // date — a disagreement the page invented rather than found, which is the one kind it must
  // not show. It also makes the rendering testable against a fixed instant.
  const now = Date.now();
  const expired = useMemo(
    () => (data ? expiredItems(data.items.planned, now) : []),
    // `now` is deliberately not a dependency: it changes on every render and would defeat the
    // memo without changing the answer, since expiry moves at the granularity of a day.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );

  /** Select the gap types one alignment direction owns, or clear if already selected. */
  function selectAlignment(row: AlignmentRow) {
    const same = row.types.length === types.size && row.types.every((t) => types.has(t));
    setTypes(same ? new Set() : new Set(row.types));
  }

  function toggle<T>(set: Set<T>, value: T, apply: (s: Set<T>) => void) {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    apply(next);
  }

  const stampAge = ageText(data?.deployed.stampedAt);

  return (
    <div className="min-h-screen bg-tower-bg text-tower-text flex flex-col">
      <header className="h-14 bg-tower-surface border-b border-tower-border flex items-center px-6 gap-4 shrink-0">
        <Link
          to="/"
          className="text-lg font-mono font-bold text-tower-accent tracking-wider hover:text-tower-text transition-colors"
        >
          MAESTRO
        </Link>
        <div className="h-4 w-px bg-tower-border" />
        <span className="text-sm text-tower-accent font-semibold">Roadmap</span>
        <div className="flex-1" />
        <HeaderNav />
      </header>

      <main className="flex-1 overflow-y-auto p-6">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/roadmap"
            className="text-xs text-tower-muted hover:text-tower-accent transition-colors"
          >
            &larr; The capability story
          </Link>
          <h1 className="text-2xl font-bold text-tower-accent mb-1 mt-1">Five-lane roadmap</h1>
          <p className="text-sm text-tower-muted mb-6 max-w-3xl leading-relaxed">
            What we <em>claim</em>, what we <em>planned</em>, what <em>shipped</em>, what is{' '}
            <em>deployed</em>, and what has been <em>verified</em> — joined so they can
            disagree. The gaps below are the product; the lanes are only the inputs.
          </p>

          {error && (
            <div className="p-4 mb-4 bg-error-bg border border-error rounded-lg text-sm text-tower-text">
              Could not load the roadmap ({error}). This route requires a signed-in session —
              an authentication failure and an outage look the same from here, so check{' '}
              <span className="font-mono">Sign out / Sign in</span> before assuming the API is down.
            </div>
          )}

          {!data && !error && <div className="text-sm text-tower-muted">Loading roadmap…</div>}

          {data && data.schemaVersion !== ROADMAP_SCHEMA_VERSION && (
            <div className="p-4 mb-4 bg-warning-bg border border-warning rounded-lg text-sm text-tower-text">
              This page renders roadmap schema v{ROADMAP_SCHEMA_VERSION}; the API returned v
              {data.schemaVersion}. Fields added since may not be shown — the page is not
              pretending otherwise.
            </div>
          )}

          {data && (
            <>
              {/* ── lane strip ─────────────────────────────────────────────── */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                <LaneCell name="Claimed · counts" reading={data.lanes.claimed.counts} />
                <LaneCell name="Claimed · remaining" reading={data.lanes.claimed.remaining} />
                <LaneCell name="Planned · north-star" reading={data.lanes.planned.northStar} />
                <LaneCell name="Planned · queue" reading={data.lanes.planned.queue} />
                <LaneCell name="Shipped" reading={data.lanes.shipped} />
                <LaneCell name="Deployed" reading={data.lanes.deployed} />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-8">
                <LaneCell
                  name="Verified"
                  reading={data.lanes.verified}
                  note="No lane has been checked against running code yet."
                />
                <div className="p-3 rounded-lg border bg-tower-surface border-tower-border flex items-center gap-4 flex-wrap">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-tower-muted font-mono">
                      Gaps
                    </div>
                    <div className="text-lg font-semibold text-tower-text">{data.summary.gaps}</div>
                  </div>
                  {SEVERITY_ORDER.map((s) => (
                    <span
                      key={s}
                      className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded border ${SEVERITY_STYLE[s].chip}`}
                    >
                      {SEVERITY_STYLE[s].label} {data.summary.gapsBySeverity[s] ?? 0}
                    </span>
                  ))}
                </div>
              </div>

              {/* ── the alignment view (ROADMAP_APP.md §4, Phase 3) ────────── */}
              {data.alignment && data.alignment.length > 0 && (
                <section className="mb-8">
                  <h2 className="text-sm font-semibold text-tower-text mb-1 uppercase tracking-wider font-mono">
                    Alignment — where two lanes disagree
                  </h2>
                  <p className="text-xs text-tower-muted mb-3 max-w-3xl leading-relaxed">
                    Four directions, each a different kind of wrong. Select one to filter the
                    gaps below. A direction that could not be computed says{' '}
                    <span className="font-mono">unmeasured</span> and names the missing input —
                    it does not show a zero, because nobody checked.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    {data.alignment.map((row) => (
                      <AlignmentCell
                        key={row.id}
                        row={row}
                        active={
                          row.types.length > 0 &&
                          row.types.length === types.size &&
                          row.types.every((t) => types.has(t))
                        }
                        extra={
                          // The expiry count belongs to planned→shipped and is computed on
                          // this side of the wire, so the cell labels it rather than folding
                          // it into a number the artifact would appear to vouch for.
                          row.renderedClientSide?.includes('planned-expired')
                            ? { count: expired.length, label: 'expired' }
                            : undefined
                        }
                        onSelect={() => selectAlignment(row)}
                      />
                    ))}
                  </div>
                  {data.summary.unroutedGapTypes && data.summary.unroutedGapTypes.length > 0 && (
                    <div className="mt-3 p-3 bg-warning-bg border border-warning rounded-lg text-xs text-tower-text">
                      These gap types belong to no direction above, so the counts on this strip
                      do not add up to the total:{' '}
                      <span className="font-mono">
                        {data.summary.unroutedGapTypes.join(', ')}
                      </span>
                      . Shown because arithmetic that silently fails to add up is worse than
                      arithmetic that says so.
                    </div>
                  )}
                </section>
              )}

              {/* ── expired commitments: computed here, never in the artifact ── */}
              {expired.length > 0 && (
                <section className="mb-8">
                  <h2 className="text-sm font-semibold text-tower-text mb-1 uppercase tracking-wider font-mono">
                    Expired commitments ({expired.length})
                  </h2>
                  <p className="text-xs text-tower-muted mb-3 max-w-3xl leading-relaxed">
                    <span className="font-mono">review_by</span> has passed and the item is
                    still open. Computed in the browser from{' '}
                    <span className="font-mono">reviewBy</span>, never written into{' '}
                    <span className="font-mono">roadmap.json</span> — an age baked into a
                    committed artifact reds the drift gate on a date rather than on a diff.
                  </p>
                  <div className="grid gap-2">
                    {expired.map((item) => (
                      <div
                        key={item.id}
                        data-expired={item.id}
                        className="border-l-4 border-l-warning bg-tower-surface border border-tower-border rounded-r-lg p-3"
                      >
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded border bg-warning-bg text-tower-text border-warning">
                            EXPIRED
                          </span>
                          <span className="text-xs font-mono text-tower-text">
                            review_by {item.planned?.reviewBy}
                          </span>
                          <span className="text-sm text-tower-text flex-1 min-w-[12rem]">
                            {item.title}
                          </span>
                        </div>
                        {item.cites && item.cites.length > 0 && (
                          <div className="text-[11px] text-tower-muted mt-2 leading-relaxed">
                            Cites:
                            <Cites cites={item.cites} />
                            <span className="ml-1">
                              — green means the file is in the tree today. Check whether the
                              premise still holds before re-committing to the date.
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* ── filters ────────────────────────────────────────────────── */}
              <div className="flex items-center gap-2 flex-wrap mb-4">
                {SEVERITY_ORDER.map((s) => (
                  <button
                    key={s}
                    onClick={() => toggle(severities, s, setSeverities)}
                    className={`text-[10px] font-mono font-bold px-2 py-1 rounded border transition-colors ${
                      severities.has(s)
                        ? SEVERITY_STYLE[s].chip
                        : 'bg-tower-surface text-tower-muted border-tower-border'
                    }`}
                  >
                    {SEVERITY_STYLE[s].label}
                  </button>
                ))}
                <div className="h-4 w-px bg-tower-border" />
                {allTypes.map((t) => (
                  <button
                    key={t}
                    onClick={() => toggle(types, t, setTypes)}
                    className={`text-[10px] font-mono px-2 py-1 rounded border transition-colors ${
                      types.has(t)
                        ? 'bg-primary-bg text-tower-accent border-tower-accent'
                        : 'bg-tower-surface text-tower-muted border-tower-border hover:border-tower-accent'
                    }`}
                  >
                    {t}
                  </button>
                ))}
                <div className="flex-1" />
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="filter text…"
                  className="text-xs bg-tower-surface border border-tower-border rounded px-2 py-1 text-tower-text placeholder:text-tower-muted focus:outline-none focus:border-tower-accent"
                />
              </div>

              {/* ── the gaps, which are the point ──────────────────────────── */}
              <section className="mb-10">
                <h2 className="text-sm font-semibold text-tower-text mb-3 uppercase tracking-wider font-mono">
                  Disagreements ({gaps.length}
                  {gaps.length !== data.gaps.length && ` of ${data.gaps.length}`})
                </h2>
                {gaps.length === 0 ? (
                  <div className="p-4 bg-tower-surface border border-tower-border rounded-lg text-sm text-tower-muted">
                    {data.gaps.length === 0
                      ? 'No lane disagrees with another. Note what this does NOT mean: the VERIFIED lane is unmeasured, so this is agreement between documents, not agreement with running code.'
                      : 'No gap matches the current filters.'}
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {gaps.map((g, i) => (
                      <GapRow key={`${g.type}:${g.itemId || g.title || i}`} gap={g} />
                    ))}
                  </div>
                )}
              </section>

              {/* ── counts: claimed vs baseline ────────────────────────────── */}
              <section className="mb-10">
                <h2 className="text-sm font-semibold text-tower-text mb-3 uppercase tracking-wider font-mono">
                  Claimed counts vs north-star baselines
                </h2>
                <div className="overflow-x-auto bg-tower-surface border border-tower-border rounded-lg p-4">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-tower-border text-[10px] uppercase tracking-wider text-tower-muted font-mono">
                        <th className="pb-2 pr-3">Label</th>
                        <th className="pb-2 pr-3">Claimed (§7)</th>
                        <th className="pb-2 pr-3">Baseline (§3)</th>
                        <th className="pb-2">Join</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.claimedCounts.map((c) => {
                        const baseline = data.baselines[c.label.toLowerCase()];
                        const has = baseline !== undefined;
                        const agree = has && baseline === c.claimed;
                        return (
                          <tr key={c.label} className="border-b border-tower-border">
                            <td className="py-2 pr-3 text-sm text-tower-text">{c.label}</td>
                            <td className="py-2 pr-3 text-sm font-mono text-tower-text">{c.claimed}</td>
                            <td className="py-2 pr-3 text-sm font-mono">
                              {has ? (
                                <span className={agree ? 'text-tower-text' : 'text-error font-bold'}>
                                  {baseline}
                                </span>
                              ) : (
                                // Again: no zero, no dash-that-reads-as-zero. There is no
                                // §3 row with this label, which is a different fact from
                                // "§3 says none".
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-info-bg text-tower-text border border-info">
                                  no baseline row
                                </span>
                              )}
                            </td>
                            <td className="py-2">
                              <span
                                className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${
                                  !has
                                    ? 'bg-info-bg text-tower-text border-info'
                                    : agree
                                      ? 'bg-success-bg text-tower-text border-success'
                                      : 'bg-error-bg text-tower-text border-error'
                                }`}
                              >
                                {!has ? 'unverifiable' : agree ? 'exact' : 'drift'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* ── deployed tiers ─────────────────────────────────────────── */}
              <section className="mb-10">
                <h2 className="text-sm font-semibold text-tower-text mb-3 uppercase tracking-wider font-mono">
                  Deployed
                  {/* The subtraction happens here, not in the artifact — see rule 2. */}
                  {stampAge && (
                    <span className="ml-2 normal-case tracking-normal text-tower-muted font-normal">
                      stamped {stampAge}
                      <span className="font-mono ml-1">({data.deployed.stampedAt})</span>
                    </span>
                  )}
                </h2>
                <div className="bg-tower-surface border border-tower-border rounded-lg p-4">
                  {data.deployed.state !== 'read' ? (
                    <div className="text-sm text-warning">
                      {data.deployed.state}
                      {data.deployed.detail && (
                        <span className="text-tower-muted"> — {data.deployed.detail}</span>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="grid gap-2">
                        {Object.entries(data.deployed.repos ?? {}).flatMap(([repo, tiers]) =>
                          Object.entries(tiers).map(([tier, sha]) => {
                            const key = `${repo}/${tier}`;
                            const onRef = data.deployed.onRef?.[key];
                            return (
                              <div key={key} className="flex items-center gap-3 flex-wrap text-xs">
                                <span className="font-mono text-tower-text w-64 shrink-0">{key}</span>
                                <span className="font-mono text-tower-muted">{sha.slice(0, 12)}</span>
                                <span
                                  className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${
                                    onRef === true
                                      ? 'bg-success-bg text-tower-text border-success'
                                      : onRef === false
                                        ? 'bg-error-bg text-tower-text border-error'
                                        : 'bg-info-bg text-tower-text border-info'
                                  }`}
                                >
                                  {onRef === true
                                    ? 'on origin/dev'
                                    : onRef === false
                                      ? 'NOT on origin/dev'
                                      : 'unknown'}
                                </span>
                              </div>
                            );
                          }),
                        )}
                      </div>
                      {(data.deployed.unknown ?? []).map((u, i) => (
                        <p
                          key={i}
                          className="text-[11px] text-tower-muted leading-relaxed mt-3 border-t border-tower-border pt-3"
                        >
                          <span className="font-mono text-info">unknown:</span> {u}
                        </p>
                      ))}
                    </>
                  )}
                </div>
              </section>

              {/* ── the items behind the lanes ─────────────────────────────── */}
              <section className="mb-10">
                <h2 className="text-sm font-semibold text-tower-text mb-3 uppercase tracking-wider font-mono">
                  Planned and claimed-remaining work ({items.length})
                </h2>
                <div className="overflow-x-auto bg-tower-surface border border-tower-border rounded-lg p-4">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-tower-border text-[10px] uppercase tracking-wider text-tower-muted font-mono">
                        <th className="pb-2 pr-3">Item</th>
                        <th className="pb-2 pr-3">Source</th>
                        <th className="pb-2 pr-3">State</th>
                        <th className="pb-2 pr-3">Owner</th>
                        <th className="pb-2 pr-3">Review by</th>
                        <th className="pb-2">Verified</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((i) => (
                        <ItemRow key={i.id} item={i} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* ── provenance ─────────────────────────────────────────────── */}
              <section>
                <h2 className="text-sm font-semibold text-tower-text mb-3 uppercase tracking-wider font-mono">
                  Sources
                </h2>
                <div className="bg-tower-surface border border-tower-border rounded-lg p-4 grid gap-2">
                  {data.sources.map((s) => (
                    <div key={s.name} className="flex items-center gap-3 flex-wrap text-xs">
                      <span className="font-mono text-tower-text w-40 shrink-0">{s.name}</span>
                      <span className="font-mono text-tower-muted flex-1 min-w-[14rem]">{s.path}</span>
                      {s.state === 'read' ? (
                        <>
                          {/* The hash is of the BYTES READ, not `git log -1 -- path`:
                              a local edit changes it, which is the point. */}
                          <span className="font-mono text-tower-muted">{s.sha256}</span>
                          <span className="font-mono text-tower-muted">{s.bytes} B</span>
                        </>
                      ) : (
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-warning-bg text-tower-text border border-warning">
                          {s.state}
                          {s.detail ? ` — ${s.detail}` : ''}
                        </span>
                      )}
                    </div>
                  ))}
                  <p className="text-[11px] text-tower-muted leading-relaxed border-t border-tower-border pt-3 mt-1">
                    This page carries no generation timestamp on purpose. The artifact is
                    byte-identical for identical inputs, which is what lets{' '}
                    <span className="font-mono">roadmap_build.py --check</span> gate drift;
                    the source hashes above identify the exact bytes it was built from.
                  </p>
                </div>
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
